import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";

import {
  Button,
  CopyIcon,
  DownloadIcon,
  IconButton,
  OpenIcon,
  type ButtonProps,
  type ButtonTone,
} from "../../components/ui";
import {
  safeArtifactDownloadUrl,
  startArtifactDownload,
  startBlobDownload,
} from "../../lib/artifactDownload";
import { copyText } from "../../lib/clipboard";
import {
  agentArtifactLinks,
  fetchXLMWorkbook,
  useAgentArtifactLinks,
  type AgentArtifact,
} from "../api/e2e";
import { verifiedArtifactText } from "../lib/agentSummary";
import { useAgentArtifactOpenAction } from "./AgentArtifactActionContext";

const ROLE_LABELS: Record<string, string> = {
  workbook: "Download Excel",
  validation: "Validation",
  agent_summary: "Copy summary",
};

export function artifactRoleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role.replaceAll("_", " ");
}

export default function AgentArtifactActions({
  companyId,
  versionId,
  runId,
  artifacts,
  compact = false,
  compactSize,
  compactTone = "quiet",
  hideSummary = false,
  disabled = false,
}: {
  companyId: string;
  versionId: string;
  runId: string;
  artifacts: AgentArtifact[];
  compact?: boolean;
  compactSize?: ButtonProps["size"];
  compactTone?: ButtonTone;
  hideSummary?: boolean;
  disabled?: boolean;
}) {
  const links = useAgentArtifactLinks();
  const hostOpenArtifact = useAgentArtifactOpenAction();
  const [opening, setOpening] = useState<string | null>(null);
  const [prefetchedSummary, setPrefetchedSummary] = useState<string | null>(
    null,
  );
  const summaryArtifact = artifacts.find(
    (artifact) => artifact.role === "agent_summary",
  );
  const copyableSummary = prefetchedSummary;

  useEffect(() => {
    if (hideSummary || !summaryArtifact) return;
    let active = true;
    void (async () => {
      try {
        const [link] = await agentArtifactLinks(runId, [summaryArtifact.role]);
        if (!link) return;
        const url = safeArtifactDownloadUrl(link.url, window.location.origin);
        const response = await fetch(url);
        if (!response.ok) return;
        const content = await verifiedArtifactText(response, summaryArtifact);
        if (active) setPrefetchedSummary(content);
      } catch {
        // The copy button stays disabled; the surrounding page owns errors.
      }
    })();
    return () => {
      active = false;
    };
  }, [hideSummary, runId, summaryArtifact]);

  const handleArtifact = async (artifact: AgentArtifact) => {
    setOpening(artifact.role);
    try {
      if (hostOpenArtifact) {
        await hostOpenArtifact(artifact);
        return;
      }
      if (artifact.role === "agent_summary") {
        if (copyableSummary === null)
          throw new Error("The summary is still loading.");
        await copyText(copyableSummary);
        toast.success("Summary copied.");
        return;
      }
      if (artifact.role === "workbook") {
        const workbook = await fetchXLMWorkbook(companyId, versionId);
        startBlobDownload(workbook.content, workbook.filename);
        return;
      }
      const [link] = await links.mutateAsync({
        runId,
        roles: [artifact.role],
      });
      if (!link) throw new Error("The artifact link was not returned.");
      startArtifactDownload(link.url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Download failed.");
    } finally {
      setOpening(null);
    }
  };

  const visibleArtifacts = artifacts.filter(
    (artifact) =>
      artifact.role !== "model" &&
      (!hideSummary || artifact.role !== "agent_summary"),
  );
  if (visibleArtifacts.length === 0) return null;
  return (
    <div
      className={
        compact ? "agent-artifacts agent-artifacts--compact" : "agent-artifacts"
      }
    >
      {visibleArtifacts.map((artifact) => {
        const label = artifactRoleLabel(artifact.role);
        const actionDisabled =
          disabled ||
          opening !== null ||
          (artifact.role === "agent_summary" && copyableSummary === null);
        if (compact) {
          const tooltip =
            artifact.role === "workbook"
              ? "Export the current model revision to Excel."
              : artifact.role === "agent_summary"
                ? "Copy the model summary to your clipboard."
                : `Download ${artifact.filename}.`;
          return (
            <IconButton
              key={artifact.role}
              tone={compactTone}
              size={compactSize}
              label={label}
              tooltip={tooltip}
              icon={
                artifact.role === "agent_summary" ? (
                  <CopyIcon />
                ) : (
                  <DownloadIcon />
                )
              }
              disabled={actionDisabled}
              loading={opening === artifact.role}
              onClick={() => void handleArtifact(artifact)}
            />
          );
        }
        return (
          <Button
            key={artifact.role}
            tone="secondary"
            disabled={actionDisabled}
            aria-busy={opening === artifact.role}
            aria-label={label}
            title={`${artifact.filename} · ${Math.max(1, Math.round(artifact.size_bytes / 1024)).toLocaleString()} KB`}
            iconRight={
              opening !== artifact.role && artifact.role !== "agent_summary" ? (
                <OpenIcon />
              ) : undefined
            }
            onClick={() => void handleArtifact(artifact)}
          >
            {opening === artifact.role
              ? artifact.role === "agent_summary"
                ? "Copying…"
                : "Opening…"
              : label}
          </Button>
        );
      })}
    </div>
  );
}
